import React, { FC, ChangeEvent, KeyboardEvent, useRef, useEffect, useCallback, useMemo } from "react";
import classNames from "classnames";
import styles from "./AuthCodeInput.module.scss";

/**
 * The authentication input component properties.
 */
export interface AuthCodeInputProps {
  /**
   * The component id.
   */
  readonly id: string;
  /**
   * The component CSS class name.
   */
  readonly className?: string;

  /**
   * The length of the authenticator code.
   */
  readonly length?: number;

  /**
   * A regular expression defining the set of allowed characters.
   */
  readonly allowedCharacters?: RegExp;

  /**
   * The function to call when the code changes.
   */
  readonly onChange?: (value: string) => void;

  /**
   * The function to call when the input is focused.
   */
  readonly onFocus?: () => void;

  /**
   * The function to call when the input is blurred.
   */
  readonly onBlur?: () => void;

  /**
   * The form field value
   */
  readonly value: string;
}

/**
 * The authentication input component.
 */
export const AuthCodeInput: FC<AuthCodeInputProps> = (props: AuthCodeInputProps) => {
  const { className: auxClassName, length = 6, allowedCharacters = /^[0-9]*$/, onFocus, onBlur, onChange, value, id } = props;
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  useEffect(() => {
    if (inputsRef.current[0]) inputsRef.current[0].focus();
  }, []);

  const sendResult = useCallback((): void => {
    const value = inputsRef.current.map((input) => input?.value).join("");
    if (onChange) onChange(value);
  }, [onChange]);

  const changeHandler = useCallback(
    (ev: ChangeEvent<HTMLInputElement>): void => {
      const { target } = ev;
      const { value } = target;

      if (value && !value.match(allowedCharacters)) {
        target.value = "";
      } else {
        sendResult();
      }
    },
    [sendResult],
  );

  const keyDownHandler = useCallback(
    (ev: KeyboardEvent<HTMLInputElement>): void => {
      const { key } = ev;
      const target = ev.target as HTMLInputElement;
      const { value, previousElementSibling, nextElementSibling } = target;

      if (key.match(allowedCharacters) && target.value === "") {
        if (nextElementSibling !== null) {
          (nextElementSibling as HTMLInputElement).focus();
        } else {
          target.value = "";
        }
      } else if (key === "Backspace") {
        if (value === "" && previousElementSibling !== null) {
          if (previousElementSibling !== null) {
            (previousElementSibling as HTMLInputElement).focus();
            ev.preventDefault();
          }
        }
      } else if (key === "ArrowLeft") {
        if (value && value.match(allowedCharacters)) {
          if (previousElementSibling !== null) {
            (previousElementSibling as HTMLInputElement).focus();
            (previousElementSibling as HTMLInputElement).select();
            ev.preventDefault();
          }
        } else {
          target.value = "";
        }
      } else if (key === "ArrowRight") {
        if (value && value.match(allowedCharacters)) {
          if (nextElementSibling !== null) {
            (nextElementSibling as HTMLInputElement).focus();
            (nextElementSibling as HTMLInputElement).select();
            ev.preventDefault();
          }
        } else {
          target.value = "";
        }
      }
    },
    [allowedCharacters],
  );

  const pasteHandler = useCallback(
    (ev: React.ClipboardEvent<HTMLInputElement>): void => {
      const value = ev.clipboardData.getData("Text");
      if (value && value.match(allowedCharacters)) {
        for (let i = 0; i < length && i < value.length; i++) {
          const input = inputsRef.current[i] as HTMLInputElement;
          const { nextElementSibling } = input;

          if (input !== null) {
            input.value = value.charAt(i);
          }
          if (nextElementSibling !== null) {
            (nextElementSibling as HTMLInputElement).focus();
          }
        }
        sendResult();
      }
      ev.preventDefault();
    },
    [sendResult],
  );

  const focusHandler = useCallback(
    (ev: React.FocusEvent<HTMLInputElement>): void => {
      const { target } = ev;
      target.select();

      if (onFocus) onFocus();
    },
    [onFocus],
  );

  const blurHandler = useCallback((): void => {
    if (onBlur) onBlur();
  }, [onBlur]);

  const inputs = useMemo((): ReadonlyArray<JSX.Element> => {
    const result: JSX.Element[] = [];

    for (let i = 0; i < length; i++) {
      result.push(
        <input
          className="entry"
          key={i}
          value={value[i] ?? ""}
          onChange={changeHandler}
          onKeyDown={keyDownHandler}
          onFocus={focusHandler}
          onBlur={blurHandler}
          onPaste={pasteHandler}
          type="text"
          ref={(ref: HTMLInputElement | null): void => {
            inputsRef.current[i] = ref;
          }}
          maxLength={1}
          data-testid={`test__${id}-input-box-${i}`}
        />,
      );
    }

    return result;
  }, [value, length, changeHandler, keyDownHandler, focusHandler, pasteHandler]);

  const className = classNames(styles.authenticationInput, auxClassName);

  return <div className={className}>{inputs}</div>;
};

export default AuthCodeInput;
